import { Component, OnInit, LOCALE_ID } from '@angular/core';
import { CommonModule, registerLocaleData } from '@angular/common';
import localeEs from '@angular/common/locales/es';
import { FormsModule } from '@angular/forms';

registerLocaleData(localeEs);
import { CalendarModule, DateAdapter } from 'angular-calendar';
import { adapterFactory } from 'angular-calendar/date-adapters/date-fns';
import { CalendarEvent, CalendarView } from 'angular-calendar';
import { startOfMonth, endOfMonth, startOfDay, isSameDay, isSameMonth, format, startOfWeek, endOfWeek, addWeeks, subWeeks, addMonths, subMonths } from 'date-fns';
import { es } from 'date-fns/locale';
import { CalendarService } from '../../core/services/calendar.service';
import { CalendarDay, CalendarDayResponse, CalendarEventMeta } from '../../core/interfaces/calendar.interface';
import { ToastrService } from 'ngx-toastr';
import { SpinnerComponent } from '../../components/spinner/spinner.component';

@Component({
  selector: 'app-calendar',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    CalendarModule,
    SpinnerComponent
  ],
  providers: [
    {
      provide: DateAdapter,
      useFactory: adapterFactory,
    },
    { provide: LOCALE_ID, useValue: 'es' }
  ],
  templateUrl: './calendar.component.html',
  styleUrl: './calendar.component.scss'
})
export class CalendarComponent implements OnInit {
  viewDate: Date = new Date();
  view: CalendarView = CalendarView.Month;
  CalendarView = CalendarView;
  events: CalendarEvent<CalendarEventMeta>[] = [];
  
  locale: string = 'es';
  weekStartsOn: 0 | 1 | 2 | 3 | 4 | 5 | 6 = 1; // 0 = Domingo, 1 = Lunes
  
  loading = false;
  showModal = false;
  selectedDay: CalendarDayResponse | null = null;
  editingDay: CalendarDayResponse = this.getEmptyDay();
  
  calendarDays: CalendarDay[] = [];
  weekDays: { date: Date; calendarDay?: CalendarDay }[] = [];
  weekDayNames: string[] = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];
  
  dayStartHour = 6;
  dayEndHour = 20;
  hourSegments = 2;

  constructor(
    private calendarService: CalendarService,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    this.loadCalendarData();
  }

  loadCalendarData(): void {
    this.loading = true;
    this.events = []; // Limpiar eventos antes de cargar nuevos

    let startDate: string;
    let endDate: string;

    if (this.view === CalendarView.Month) {
      startDate = format(startOfMonth(this.viewDate), 'yyyy-MM-dd');
      endDate = format(endOfMonth(this.viewDate), 'yyyy-MM-dd');
    } else {
      // Vista semanal
      startDate = format(startOfWeek(this.viewDate, { weekStartsOn: 1 }), 'yyyy-MM-dd');
      endDate = format(endOfWeek(this.viewDate, { weekStartsOn: 1 }), 'yyyy-MM-dd');
    }

    console.log('Loading calendar data:', { startDate, endDate });

    this.calendarService.getCalendar(startDate, endDate).subscribe({
      next: (data) => {
        console.log('Calendar data received:', data);
        this.calendarDays = data.map((day: any) => ({
          ...day,
          date: new Date(day.date)
        }));
        
        if (this.view === CalendarView.Week) {
          this.generateWeekDays();
        }
        
        this.adjustDayHours();
        this.updateEvents();
        this.loading = false;
      },
      error: (err) => {
        console.error('Error loading calendar:', err);
        this.toastr.error('Error al cargar el calendario');
        this.loading = false;
      }
    });
  }

  generateWeekDays(): void {
    const weekStart = startOfWeek(this.viewDate, { weekStartsOn: 1 });
    this.weekDays = [];
    
    for (let i = 0; i < 7; i++) {
      const date = new Date(weekStart);
      date.setDate(weekStart.getDate() + i);
      
      const calendarDay = this.calendarDays.find(d => isSameDay(d.date, date));
      
      this.weekDays.push({
        date,
        calendarDay
      });
    }
  }

  adjustDayHours(): void {
    if (this.view === CalendarView.Week && this.calendarDays.length > 0) {
      let minHour = 24;
      let maxHour = 0;

      this.calendarDays.forEach(day => {
        if (day.type === 'laborable' && day.start_hour && day.end_hour) {
          const startHour = parseInt(day.start_hour.split(':')[0], 10);
          const endHour = parseInt(day.end_hour.split(':')[0], 10);
          
          if (startHour < minHour) minHour = startHour;
          if (endHour > maxHour) maxHour = endHour;
        }
      });

      // Ajustar con margen de 1 hora antes y después
      if (minHour < 24) {
        this.dayStartHour = Math.max(0, minHour - 1);
        this.dayEndHour = Math.min(24, maxHour + 1);
      }
    }
  }

  updateEvents(): void {
    const newEvents = this.calendarDays.map(calendarDay => {
      const isWorkingDay = calendarDay.type === 'laborable';
      let title = '';
      let eventStart: Date;
      let eventEnd: Date | undefined;
      
      if (isWorkingDay && calendarDay.start_hour && calendarDay.end_hour) {
        const startTime = calendarDay.start_hour.substring(0, 5);
        const endTime = calendarDay.end_hour.substring(0, 5);
        title = `${startTime} - ${endTime}`;
        
        // Para la vista semanal, crear eventos con hora de inicio y fin
        const [startHour, startMinute] = calendarDay.start_hour.split(':').map(Number);
        const [endHour, endMinute] = calendarDay.end_hour.split(':').map(Number);
        
        eventStart = new Date(calendarDay.date);
        eventStart.setHours(startHour, startMinute, 0, 0);
        
        eventEnd = new Date(calendarDay.date);
        eventEnd.setHours(endHour, endMinute, 0, 0);
      } else if (!isWorkingDay) {
        title = 'Festivo';
        eventStart = startOfDay(calendarDay.date);
        // Para días festivos, ocupar todo el día en la vista semanal
        eventEnd = new Date(calendarDay.date);
        eventEnd.setHours(23, 59, 59, 999);
      } else {
        eventStart = startOfDay(calendarDay.date);
      }
      
      const event: CalendarEvent<CalendarEventMeta> = {
        start: eventStart,
        end: eventEnd,
        title: title,
        color: {
          primary: isWorkingDay ? '#004e9e' : '#e3000f',
          secondary: isWorkingDay ? '#e3f5ff' : '#ffecee',
        },
        meta: {
          calendarDay
        }
      };
      return event;
    });
    
    // Crear nueva referencia del array para forzar detección de cambios
    this.events = [...newEvents];
    console.log('Events updated:', this.events.length);
  }

  dayClicked({ date, events }: { date: Date; events: CalendarEvent<CalendarEventMeta>[] }): void {
    if (isSameMonth(date, this.viewDate)) {
      const existingDay = this.calendarDays.find(d => isSameDay(d.date, date));
      
      if (existingDay) {
        this.editingDay = {
          id: existingDay.id,
          date: format(existingDay.date, 'yyyy-MM-dd'),
          type: existingDay.type,
          start_hour: existingDay.start_hour,
          end_hour: existingDay.end_hour
        };
      } else {
        this.editingDay = {
          date: format(date, 'yyyy-MM-dd'),
          type: 'laborable',
          start_hour: '08:00:00',
          end_hour: '17:00:00'
        };
      }
      
      this.showModal = true;
    }
  }

  closeModal(): void {
    this.showModal = false;
    this.editingDay = this.getEmptyDay();
  }

  saveDay(): void {
    if (!this.validateDay()) {
      return;
    }

    this.loading = true;
    this.calendarService.updateCalendarDay(this.editingDay).subscribe({
      next: (savedDay) => {
        this.toastr.success('Día guardado correctamente');
        this.closeModal();
        this.loadCalendarData();
      },
      error: (err) => {
        console.error('Error saving day:', err);
        this.toastr.error('Error al guardar el día');
        this.loading = false;
      }
    });
  }


  validateDay(): boolean {
    if (this.editingDay.type === 'laborable') {
      if (!this.editingDay.start_hour || !this.editingDay.end_hour) {
        this.toastr.warning('Debes especificar hora de inicio y fin para días laborables');
        return false;
      }
      
      if (this.editingDay.start_hour >= this.editingDay.end_hour) {
        this.toastr.warning('La hora de fin debe ser posterior a la hora de inicio');
        return false;
      }
    }
    return true;
  }

  previousMonth(): void {
    if (this.view === CalendarView.Month) {
      this.viewDate = subMonths(this.viewDate, 1);
    } else {
      this.viewDate = subWeeks(this.viewDate, 1);
    }
    this.loadCalendarData();
  }

  nextMonth(): void {
    if (this.view === CalendarView.Month) {
      this.viewDate = addMonths(this.viewDate, 1);
    } else {
      this.viewDate = addWeeks(this.viewDate, 1);
    }
    this.loadCalendarData();
  }

  todayMonth(): void {
    this.viewDate = new Date();
    this.loadCalendarData();
  }

  setView(view: CalendarView): void {
    this.view = view;
    this.loadCalendarData();
  }

  getCurrentPeriod(): string {
    if (this.view === CalendarView.Month) {
      return this.getCurrentMonthYear();
    } else {
      const start = startOfWeek(this.viewDate, { weekStartsOn: 1 });
      const end = endOfWeek(this.viewDate, { weekStartsOn: 1 });
      return `${format(start, 'd MMM', { locale: es })} - ${format(end, 'd MMM yyyy', { locale: es })}`;
    }
  }

  getEmptyDay(): CalendarDayResponse {
    return {
      date: format(new Date(), 'yyyy-MM-dd'),
      type: 'laborable',
      start_hour: '08:00:00',
      end_hour: '17:00:00'
    };
  }

  formatDate(date: string | Date): string {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return dateObj.toLocaleDateString('es-ES', { 
      day: '2-digit', 
      month: 'long', 
      year: 'numeric' 
    });
  }

  getCurrentMonthYear(): string {
    const monthYear = this.viewDate.toLocaleDateString('es-ES', { 
      month: 'long', 
      year: 'numeric' 
    }).replace(' de ', ' ');
    return monthYear.charAt(0).toUpperCase() + monthYear.slice(1);
  }

  // Convertir HH:MM:SS a HH:MM para el input
  getTimeValue(time?: string): string {
    if (!time) return '';
    return time.substring(0, 5);
  }

  // Convertir HH:MM del input a HH:MM:SS para el backend
  setStartTime(event: any): void {
    const value = event.target.value;
    this.editingDay.start_hour = value ? `${value}:00` : undefined;
  }

  setEndTime(event: any): void {
    const value = event.target.value;
    this.editingDay.end_hour = value ? `${value}:00` : undefined;
  }

  isToday(date: Date): boolean {
    return isSameDay(date, new Date());
  }

  getDayColor(day: { date: Date; calendarDay?: CalendarDay }): string {
    if (!day.calendarDay) {
      return 'transparent';
    }
    
    const isWorkingDay = day.calendarDay.type === 'laborable';
    return isWorkingDay ? '#e3f5ff' : '#ffecee';
  }

  getDayInfo(day: { date: Date; calendarDay?: CalendarDay }): string | null {
    if (!day.calendarDay) {
      return null;
    }

    const isWorkingDay = day.calendarDay.type === 'laborable';
    
    if (isWorkingDay && day.calendarDay.start_hour && day.calendarDay.end_hour) {
      const startTime = day.calendarDay.start_hour.substring(0, 5);
      const endTime = day.calendarDay.end_hour.substring(0, 5);
      return `${startTime} - ${endTime}`;
    } else if (!isWorkingDay) {
      return 'Festivo';
    }
    
    return null;
  }

  onWeekDayClick(day: { date: Date; calendarDay?: CalendarDay }): void {
    if (day.calendarDay) {
      this.editingDay = {
        id: day.calendarDay.id,
        date: format(day.calendarDay.date, 'yyyy-MM-dd'),
        type: day.calendarDay.type,
        start_hour: day.calendarDay.start_hour,
        end_hour: day.calendarDay.end_hour
      };
    } else {
      this.editingDay = {
        date: format(day.date, 'yyyy-MM-dd'),
        type: 'laborable',
        start_hour: '08:00:00',
        end_hour: '17:00:00'
      };
    }
    
    this.showModal = true;
  }
}
